import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Upload, Button, message, Card, Space, Modal,
  Popconfirm
} from 'antd';
import {
  UploadOutlined, MobileOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import ImageViewer from './ImageViewer';

const { Dragger } = Upload;

const AttachmentUpload = forwardRef(({ value = [], onChange, leadId, interviewId, employeeId, compact = false, showQrButton = true, autoCreateFollowUp = false, hideList = false, onAfterAutoFollowUp, attachToLead = true }, ref) => {
  const [fileList, setFileList] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [qrCodeData, setQrCodeData] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  // 移除强制更新计数，避免列表反复重新挂载导致闪动
  const [isPolling, setIsPolling] = useState(false); // 轮询状态
  const [pollingCount, setPollingCount] = useState(0); // 轮询计数器
  const createdFollowUpIdsRef = useRef(new Set()); // 已创建过跟进的文件id集合
  const notifiedFileIdsRef = useRef(new Set()); // 已提示过的文件id集合，避免重复 message
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });

  // 保持对最新文件列表的引用，避免闭包导致的旧值
  const fileListRef = useRef(fileList);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  // 同步外部value到内部fileList
  useEffect(() => {
    if (value && value !== fileList) {
      setFileList(value);
      fileListRef.current = value;
    }
  }, [value]);

  // 初始化：如果传入 leadId / interviewId / employeeId，预加载已有关联的附件
  useEffect(() => {
    const loadExisting = async () => {
      if (attachToLead && leadId) {
        try {
          const res = await api.get(`/upload/lead/${leadId}`);
          const existing = (res.data?.data || []).map((f) => ({
            uid: f.id,
            name: f.originalName || f.id,
            status: 'done',
            url: f.url,
            thumbUrl: f.isImage ? f.url : undefined,
            size: f.size || 0,
            type: f.mimetype || (f.isImage ? 'image/*' : ''),
            fileData: { id: f.id, mimetype: f.mimetype }
          }));
          setFileList(existing);
          fileListRef.current = existing;
          onChange?.(existing);
        } catch (e) {
          // 静默
        }
      } else if (interviewId) {
        try {
          const res = await api.get(`/upload/interview/${interviewId}`);
          const existing = (res.data?.data || []).map((f) => ({
            uid: f.id,
            name: f.originalName || f.id,
            status: 'done',
            url: f.url,
            thumbUrl: f.isImage ? f.url : undefined,
            size: f.size || 0,
            type: f.mimetype || (f.isImage ? 'image/*' : ''),
            fileData: { id: f.id, mimetype: f.mimetype }
          }));
          setFileList(existing);
          fileListRef.current = existing;
          onChange?.(existing);
        } catch (e) {}
      } else if (employeeId) {
        try {
          const res = await api.get(`/upload/employee/${employeeId}`);
          const existing = (res.data?.data || []).map((f) => ({
            uid: f.id,
            name: f.originalName || f.id,
            status: 'done',
            url: f.url,
            thumbUrl: f.isImage ? f.url : undefined,
            size: f.size || 0,
            type: f.mimetype || (f.isImage ? 'image/*' : ''),
            fileData: { id: f.id, mimetype: f.mimetype }
          }));
          setFileList(existing);
          fileListRef.current = existing;
          onChange?.(existing);
        } catch (e) {}
      }
    };
    loadExisting();
  }, [leadId, interviewId, employeeId, attachToLead]);



  useImperativeHandle(ref, () => ({
    openQRCode: () => generateQRCode(),
  }));

  // 处理文件上传
  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    if (attachToLead && leadId) {
      formData.append('leadId', leadId);
    }
    if (interviewId) {
      formData.append('interviewId', interviewId);
    }
    if (employeeId) {
      formData.append('employeeId', employeeId);
    }

    setUploading(true);
    setUploadProgress({ [file.uid]: 0 });

    try {
      const response = await api.post('/upload/attachment', formData, {
        // 不手动设置Content-Type，让浏览器自动设置正确的boundary
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => ({
            ...prev,
            [file.uid]: percentCompleted
          }));
        },
      });

      if (response.data.success) {
        const newFile = {
          uid: response.data.data.id || file.uid, // 优先使用服务器返回的文件ID
          name: file.name,
          status: 'done',
          url: response.data.data?.id ? `/api/upload/download/${response.data.data.id}` : (response.data.data.qrCodeUrl || ''),
          fileData: response.data.data,
          size: file.size,
          type: file.type
        };
        if (!hideList) {
          const newFileList = [...fileList, newFile];
          setFileList(newFileList);
          onChange?.(newFileList);
        }
        message.success(`${file.name} 上传成功`);

        // 自动创建图片类型的跟进记录（使用下载URL作为内容）
        if (autoCreateFollowUp && leadId && response.data?.data?.id && file.type?.startsWith('image/')) {
          const downloadUrl = `/api/upload/download/${response.data.data.id}`;
          try {
            await api.post(`/leads/${leadId}/follow-ups`, { content: downloadUrl });
            onAfterAutoFollowUp && onAfterAutoFollowUp();
          } catch (e) {
            console.warn('创建图片跟进记录失败:', e?.message || e);
          }
        }
      } else {
        message.error(response.data.message || '上传失败');
      }
    } catch (error) {
      console.error('上传失败:', error);
      message.error('上传失败，请重试');
    } finally {
      setUploading(false);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[file.uid];
        return newProgress;
      });
    }
  };

  // 生成二维码
  const generateQRCode = async () => {
    try {
      const payload = {};
      if (attachToLead && leadId) {
        payload.leadId = leadId;
      } else if (employeeId) {
        payload.employeeId = employeeId;
      } else if (interviewId) {
        payload.interviewId = interviewId;
      }
      const response = await api.post('/upload/generate-qr', payload);

      if (response.data.success) {
        setQrCodeData(response.data.data);
        setQrModalVisible(true);
        
        // 记录最后一次上传会话ID，供创建线索后绑定
        try { window.__LAST_UPLOAD_ID__ = response.data.data.uploadId; } catch (e) {}
        
        // 开始轮询检查手机端上传的文件
        startPolling(response.data.data.uploadId);
      } else {
        message.error(response.data.message || '二维码生成失败');
      }
    } catch (error) {
      console.error('生成二维码失败:', error);
      message.error('二维码生成失败');
    }
  };

  // 开始轮询
  const startPolling = useCallback((uploadId) => {
    console.log('开始轮询，uploadId:', uploadId);
    setIsPolling(true);
    setPollingCount(0);
    const interval = setInterval(async () => {
      setPollingCount(prev => prev + 1);
      console.log(`第${pollingCount + 1}次轮询开始`);
      try {
        const response = await api.get(`/upload/mobile-files/${uploadId}?t=${Date.now()}`);
        console.log('轮询响应:', response.data);
        console.log('响应数据类型:', typeof response.data.data);
        console.log('响应数据是否为数组:', Array.isArray(response.data.data));
        
        if (response.data.success && response.data.data) {
          const fileInfos = response.data.data;
          console.log('服务器返回的文件信息:', fileInfos);
          console.log('文件信息类型:', typeof fileInfos);
          console.log('文件信息是否为数组:', Array.isArray(fileInfos));
          console.log('文件信息长度:', fileInfos.length);
          console.log('当前文件列表(最新):', fileListRef.current);
          
          // 处理多文件上传
          if (Array.isArray(fileInfos) && fileInfos.length > 0) {
            console.log('检测到文件数组，文件数量:', fileInfos.length);
            
            // 使用文件ID作为唯一标识符
            const newFiles = fileInfos.map(fileData => ({
              uid: fileData.id, // 使用文件ID作为uid，确保唯一性
              name: fileData.originalName,
              status: 'done',
              size: fileData.size,
              type: fileData.mimetype,
              fileData: fileData,
              // 添加正确的URL用于预览和显示
              url: `/api/upload/download/${fileData.id}`,
              thumbUrl: fileData.mimetype.startsWith('image/') ? `/api/upload/download/${fileData.id}` : undefined
            }));

            // 过滤掉已经存在的文件（避免重复）
            const existingFileIds = (fileListRef.current || []).map(f => f.fileData?.id).filter(Boolean);
            console.log('已存在的文件ID:', existingFileIds);
            console.log('服务器返回的所有文件ID:', fileInfos.map(f => f.id));
            
            const uniqueNewFiles = newFiles.filter(newFile => !existingFileIds.includes(newFile.fileData.id));
            
            console.log('新增的独特文件:', uniqueNewFiles.map(f => f.name));

            if (uniqueNewFiles.length > 0) {
              const newFileList = [...(fileListRef.current || []), ...uniqueNewFiles];
              console.log('更新文件列表，从', fileListRef.current?.length, '到', newFileList.length);
              console.log('新文件列表详情:', newFileList.map(f => ({ name: f.name, type: f.type, url: f.url })));
              setFileList(newFileList);
              fileListRef.current = newFileList; // 及时同步，避免下一轮使用旧值
              onChange?.(newFileList);
              
              // 仅对未提示过的新增文件进行提示
              const notYetNotified = uniqueNewFiles.filter(f => !notifiedFileIdsRef.current.has(f.fileData.id));
              if (notYetNotified.length > 0) {
                message.success(`${notYetNotified.length} 个文件已从手机端上传成功`);
                notYetNotified.forEach(f => notifiedFileIdsRef.current.add(f.fileData.id));
              }

              // 为每个新文件创建图片跟进记录
              if (autoCreateFollowUp && leadId) {
                for (const f of uniqueNewFiles) {
                  if (f.type?.startsWith('image/') || f.fileData?.mimetype?.startsWith('image/')) {
                    const downloadUrl = `/api/upload/download/${f.fileData.id}`;
                    if (!createdFollowUpIdsRef.current.has(f.fileData.id)) {
                      try {
                        await api.post(`/leads/${leadId}/follow-ups`, { content: downloadUrl });
                        createdFollowUpIdsRef.current.add(f.fileData.id);
                        onAfterAutoFollowUp && onAfterAutoFollowUp();
                      } catch (e) {
                        console.warn('创建图片跟进记录失败:', e?.message || e);
                      }
                    }
                  }
                }
              }
            } else {
              console.log('没有新的文件需要添加');
            }
          } else if (fileInfos && !Array.isArray(fileInfos)) {
            console.log('检测到单个文件对象');
            // 兼容旧格式：单个文件对象
            const fileData = fileInfos;
            const newFile = {
              uid: fileData.id, // 使用文件ID作为uid
              name: fileData.originalName,
              status: 'done',
              size: fileData.size,
              type: fileData.mimetype,
              fileData: fileData,
              url: `/api/upload/download/${fileData.id}`,
              thumbUrl: fileData.mimetype.startsWith('image/') ? `/api/upload/download/${fileData.id}` : undefined
            };

            // 检查是否已存在
            const existingFileIds = (fileListRef.current || []).map(f => f.fileData?.id).filter(Boolean);
            console.log('单个文件检查 - 已存在的文件ID:', existingFileIds);
            console.log('单个文件检查 - 当前文件ID:', fileData.id);
            
            if (!existingFileIds.includes(fileData.id)) {
              const newFileList = [...(fileListRef.current || []), newFile];
              setFileList(newFileList);
              onChange?.(newFileList);
              fileListRef.current = newFileList;

              // 仅在未提示过该文件时提示
              if (!notifiedFileIdsRef.current.has(fileData.id)) {
                message.success('1 个文件已从手机端上传成功');
                notifiedFileIdsRef.current.add(fileData.id);
              }

              console.log('新增文件:', newFile.name);

              // 为新增文件创建图片跟进记录
              if (autoCreateFollowUp && leadId) {
                const downloadUrl = `/api/upload/download/${newFile.fileData.id}`;
                if (!createdFollowUpIdsRef.current.has(newFile.fileData.id)) {
                  try {
                    await api.post(`/leads/${leadId}/follow-ups`, { content: downloadUrl });
                    createdFollowUpIdsRef.current.add(newFile.fileData.id);
                    onAfterAutoFollowUp && onAfterAutoFollowUp();
                  } catch (e) {
                    console.warn('创建图片跟进记录失败:', e?.message || e);
                  }
                }
              }
            } else {
              console.log('文件已存在，跳过:', newFile.name);
            }
          } else {
            console.log('没有文件信息');
          }
          
          // 继续轮询，因为可能还有更多文件在上传
        }
      } catch (error) {
        // 文件还未上传，继续轮询
        console.log('轮询中...', error.message);
      }
    }, 1000); // 每1秒检查一次

    setPollingInterval(interval);
  }, [onChange, autoCreateFollowUp, leadId, hideList, onAfterAutoFollowUp]);

  // 停止轮询
  const stopPolling = useCallback(() => {
    console.log('停止轮询');
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsPolling(false);
    setPollingCount(0);
  }, [pollingInterval]);



  // 删除文件
  const handleRemove = async (file) => {
    try {
      // 若有绑定信息则调用解绑
      if (attachToLead && leadId && file?.fileData?.id) {
        await api.post('/upload/unbind', { fileId: file.fileData.id, leadId });
      }
    } catch (e) { /* 忽略错误 */ }
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    onChange?.(newFileList);
    message.success('文件已删除');
  };



  // 打开图片预览
  const openImageViewer = (imageUrl, imageName, downloadUrl) => {
    if (imageUrl) {
      setCurrentImage({ 
        url: imageUrl, 
        name: imageName || '图片', 
        downloadUrl: downloadUrl || imageUrl 
      });
      setImageViewerVisible(true);
    }
  };

  // 关闭图片预览
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  // 预览文件
  const handlePreview = (file) => {
    const isImage = file.type?.startsWith('image/') || file.fileData?.mimetype?.startsWith('image/');
    if (isImage) {
      const imageUrl = file.url || file.thumbUrl || (file.fileData ? `/api/upload/download/${file.fileData.id}` : null) || file.fileData?.path;
      
      if (imageUrl) {
        openImageViewer(imageUrl, file.name, imageUrl);
      } else {
        message.warning('图片预览链接不可用');
      }
    } else {
      message.info('此文件类型不支持预览');
    }
  };

  // 上传配置
  const uploadProps = {
    name: 'file',
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => {
      // 检查文件大小 (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB!');
        return false;
      }

      // 检查文件类型
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
        'application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        message.error('不支持的文件格式!');
        return false;
      }

      handleUpload(file);
      return false; // 阻止默认上传行为
    },
  };



  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      setIsPolling(false);
    };
  }, [pollingInterval]);

  return (
    <div>
      {/* 上传区域 - 紧凑模式 */}
      <div style={{ marginBottom: compact ? 8 : 16 }}>
        <Dragger {...uploadProps} disabled={uploading} style={{ padding: compact ? 6 : 16 }}>
          {compact ? (
            <div style={{ fontSize: 12, color: '#666' }}>点击上传/拖拽文件（≤10MB）</div>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">支持图片、PDF、Word、Excel等格式，单个文件不超过10MB</p>
            </>
          )}
        </Dragger>
      </div>

      {/* 手机上传按钮（可隐藏用于外部布局） */}
      {showQrButton && (
        <div style={{ textAlign: 'center', marginBottom: compact ? 8 : 16 }}>
          <Button 
            type="primary" 
            icon={<MobileOutlined />}
            onClick={generateQRCode}
            style={{ width: '100%' }}
            size={compact ? 'middle' : 'large'}
          >
            📱 手机扫码上传
          </Button>
          {!compact && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
              扫描二维码，用手机直接上传文件
            </p>
          )}
        </div>
      )}

      {/* 文件列表 */}
      {!hideList && (
        <div>
          {fileList.length > 0 ? (
            <Card size="small" style={{ marginTop: compact ? 8 : 16 }} title={`已上传文件 (${fileList.length})`}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, padding: '8px 0' }}>
                {fileList.map((file) => {
                  const isImage = file.type?.startsWith('image/') || file.fileData?.mimetype?.startsWith('image/');
                  const imageUrl = file.url || file.thumbUrl || (file.fileData ? `/api/upload/download/${file.fileData.id}` : '') || file.fileData?.path;
                  
                  return (
                  <div key={file.uid} className="file-item" style={{ position: 'relative', border: '1px solid #d9d9d9', borderRadius: 8, padding: 8, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s', backgroundColor: '#fafafa' }}>
                    <div style={{ marginBottom: 8 }}>
                      {isImage ? (
                        <img 
                        src={imageUrl}
                        alt={file.name}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e8e8e8' }}
                        onClick={() => openImageViewer(imageUrl, file.name, imageUrl)}
                        onError={(e) => {
                          console.error('图片加载失败:', imageUrl);
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                        }}
                      />
                      ) : (
                        <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', backgroundColor: '#f5f5f5', borderRadius: 4, border: '1px solid #e8e8e8' }}>📎</div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#333', fontWeight: 500, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    <div>
                      <Space size={4}>
                        <Button size="small" onClick={() => handlePreview(file)}>预览</Button>
                        <Popconfirm title="确认删除此附件？" onConfirm={() => handleRemove(file)} okText="删除" cancelText="取消">
                          <Button size="small" danger>删除</Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  </div>
                );
                })}
              </div>
            </Card>
          ) : (
            <div style={{ marginTop: compact ? 8 : 16, padding: '16px', textAlign: 'center', color: '#999', background: '#f9f9f9', borderRadius: '4px' }}>
              暂无上传文件
            </div>
          )}
        </div>
      )}

      {/* 二维码模态框 */}
      <Modal
        title="📱 手机扫码上传"
        open={qrModalVisible}
        onCancel={() => { setQrModalVisible(false); stopPolling(); }}
        footer={[
          <Button key="close" onClick={() => { setQrModalVisible(false); stopPolling(); }}>关闭</Button>
        ]}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            <h4>手机扫码上传文件</h4>
            <p style={{ color: '#666' }}>使用手机扫描下方二维码，即可在手机上直接上传文件</p>
          </div>
          {qrCodeData && qrCodeData.qrCodeUrl ? (
            <div>
              <img src={qrCodeData.qrCodeUrl} alt="二维码" style={{ width: 200, height: 200 }} />
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: '12px', color: '#999' }}>扫描二维码后，在手机端选择文件或拍照上传</p>
                {isPolling && (<p style={{ fontSize: '11px', color: '#52c41a', marginTop: 4 }}>{`🔄 轮询进行中... (第${pollingCount}次)`}</p>)}
              </div>
            </div>
          ) : (
            <div style={{ padding: 20 }}>
              <p>二维码生成中...</p>
            </div>
          )}
        </div>
      </Modal>

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
  );
});

export default AttachmentUpload;
